import React, {FC} from 'react'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import moment from 'moment'

interface TrendChartProps {
  data: Array<{
    date: string
    totalReviews: number
    averageRating: number
    npsScore: number
  }>
  grouping: 'day' | 'month' | 'year'
}

const TrendChart: FC<TrendChartProps> = ({data, grouping}) => {
  // Formatação das datas do Eixo X dependendo do agrupamento
  const formatXAxis = (dateStr: string) => {
    if (grouping === 'day') return moment(dateStr).format('DD/MM/YYYY')
    if (grouping === 'month') return moment(dateStr).format('MMM YYYY')
    return dateStr // Ano
  }

  const categories = data.map((d) => formatXAxis(d.date))
  const volumeData = data.map((d) => d.totalReviews)
  const averageData = data.map((d) => Number(d.averageRating.toFixed(1)))
  const npsData = data.map((d) => d.npsScore)

  const series = [
    {
      name: 'Total de Respostas',
      type: 'column',
      data: volumeData,
    },
    {
      name: 'Nota Média',
      type: 'line',
      data: averageData,
    },
    {
      name: 'NPS',
      type: 'line',
      data: npsData,
    },
  ]

  const options: ApexOptions = {
    chart: {
      id: 'trend-chart',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    stroke: {
      width: [0, 3, 3], // 0 para a coluna, 3 para as linhas
      curve: 'smooth',
    },
    colors: ['#009EF7', '#FFC700', '#50CD89'], // Primary (Azul), Warning (Amarelo), Success (Verde)
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: '#A1A5B7',
          fontSize: '12px',
        },
      },
      axisBorder: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: [
      {
        seriesName: 'Total de Respostas',
        min: 0,
        title: {
          text: 'Volume de Respostas',
          style: {
            color: '#009EF7',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (val) => Math.round(val).toString(),
          style: {
            colors: '#009EF7',
          },
        },
      },
      {
        seriesName: 'Nota Média',
        opposite: true,
        min: 0,
        max: 5, // Escala de estrelas 1 a 5
        title: {
          text: 'Nota Média (0-5)',
          style: {
            color: '#FFC700',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (val) => val.toFixed(1),
          style: {
            colors: '#FFC700',
          },
        },
      },
      {
        seriesName: 'NPS',
        opposite: true,
        min: -100,
        max: 100, // Escala NPS
        title: {
          text: 'NPS Score',
          style: {
            color: '#50CD89',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (val) => Math.round(val).toString(),
          style: {
            colors: '#50CD89',
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '30%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y, {seriesIndex}) => {
          if (typeof y !== 'undefined') {
            if (seriesIndex === 1) return y.toFixed(1) + ' Estrelas' // Nota Média
            if (seriesIndex === 2) return y.toFixed(0) + ' Pontos' // NPS
            return y.toFixed(0) + ' Respostas' // Volume
          }
          return y
        },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      labels: {
        colors: '#7E8299',
      },
    },
    grid: {
      borderColor: '#E4E6EF',
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }

  return (
    <div id='chart'>
      <Chart options={options} series={series} type='line' height={350} />
    </div>
  )
}

export default TrendChart
